import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "sonner";
import { RefreshCw } from "lucide-react";

// Componentes refactorizados
import Landing from "./components/app/Landing";
import LoginPanel from "./components/app/LoginPanel";
import CambiarPasswordPanel from "./components/app/CambiarPasswordPanel";
import PasswordReset from "./components/app/PasswordReset";
import ClientEntry from "./components/app/ClientEntry";
import CAESLanding from "./components/app/CAESLandingNew";
import AdminPanel from "./components/app/AdminPanel";
import MenuZonas from "./components/app/MenuZonas";
import ConfiguracionConstructor from "./components/app/ConfiguracionConstructor";
import Clientes from "./components/app/Clientes";
import Reuniones from "./components/app/Reuniones";
import Ofertas from "./components/app/Ofertas"; // eslint-disable-line no-unused-vars
import ProspeccionPanel from "./components/app/ProspeccionPanel";
import QuickAnalysis from "./components/app/QuickAnalysis";
import CuestionarioDiagnostico from "./components/app/CuestionarioDiagnostico";
import Simulator from "./components/app/Simulator";
import AreaLanding from "./components/app/AreaLanding";
import CookieBanner from "./components/app/CookieBanner";
import { usePageTracking } from "./components/app/PageTracker";
import MockupResultadoParcial from "./components/app/MockupResultadoParcial";
import InformeCAES from "./components/app/InformeCAES";
import CalculadoraAhorro from "./components/app/CalculadoraAhorro";
import MockupFondoBlanco from "./components/app/MockupFondoBlanco";
import MockupFondoBlancoV2 from "./components/app/MockupFondoBlancoV2";
import MockupV2Real from "./components/app/MockupV2Real";
import MockupV2ConFotos from "./components/app/MockupV2ConFotos";
import MockupV3 from "./components/app/MockupV3";
import MockupCalcTotal from "./components/app/MockupCalcTotal";
import MockupTablasAcordeon from "./components/app/MockupTablasAcordeon";
import MockupConstructorLayout from "./components/app/MockupConstructorLayout";
import ZonaNav from "./components/app/ZonaNav";
import SaaSModule from "./components/saas/SaaSModule";
import SaaSAdmin from "./components/saas/SaaSAdmin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppContext = createContext();
export const useApp = () => useContext(AppContext);

// ============================================
// APP PRINCIPAL
// ============================================
function App() {
  // Determinar página inicial basándose en el SUBDOMINIO y la URL
  const getInitialPage = () => {
    const hostname = window.location.hostname.toLowerCase();
    const path = window.location.pathname.toLowerCase();
    
    // ========== DETECCIÓN DE SUBDOMINIO + RUTA ==========
    // Para tuingresocaes.mobilsafe.es con rutas específicas
    if (hostname.includes('tuingresocaes') || hostname.includes('tuingreso')) {
      // tuingresocaes.mobilsafe.es/analisis → Análisis de Flota
      if (path === '/analisis' || path === '/analisis/' || path === '/simulador' || path === '/simulador/') {
        return 'landing';
      }
      // IMPL-031 · tuingresocaes.mobilsafe.es/calculadora → Calculadora de Ahorro
      if (path === '/calculadora' || path === '/calculadora/') {
        return 'calculadora';
      }
      // tuingresocaes.mobilsafe.es/admin → Panel admin
      if (path === '/admin' || path === '/admin/') {
        return 'login';
      }
      // tuingresocaes.mobilsafe.es/informe/xxx → Informe CAES
      if (path.startsWith('/informe/')) {
        return 'informe';
      }
      // IMPL-067 · Rutas de admin — deben reconocerse igual que en cualquier hostname.
      // Antes: cualquier /admin/* no reconocido caía al `return 'caes'` final y expulsaba
      // al usuario a la landing pública (visible al pulsar "Catálogo productos" u otros
      // botones del AdminPanel que actualizaban location.hash disparando popstate).
      if (path === '/admin/constructor/configuracion' || path === '/admin/constructor/configuracion/') {
        return 'configuracion-constructor';
      }
      if (path === '/admin/administracion' || path === '/admin/administracion/') {
        return 'admin-panel';
      }
      if (path === '/admin/ofertas' || path === '/admin/ofertas/') {
        return 'ofertas';
      }
      if (path === '/admin/clientes' || path === '/admin/clientes/') {
        return 'clientes';
      }
      if (path === '/admin/zona1' || path === '/admin/zona1/') {
        return 'zona1';
      }
      if (path === '/admin/prospeccion' || path === '/admin/prospeccion/') {
        return 'prospeccion';
      }
      if (path === '/admin/saas/administracion' || path === '/admin/saas/administracion/') return 'saas-admin';
      if (path === '/admin/saas' || path === '/admin/saas/' || path.startsWith('/admin/saas/')) return 'saas';
      // tuingresocaes.mobilsafe.es (raíz o path no reconocido) → Landing CAES
      return 'caes';
    }
    
    // admin.mobilsafe.es → Panel de administración (login)
    if (hostname.includes('admin')) {
      return 'login';
    }
    
    // simuladorahorros.mobilsafe.es → Landing principal (simulador)
    // Este es el comportamiento por defecto, así que continúa con la lógica normal
    
    // ========== DETECCIÓN POR RUTA (comportamiento actual) ==========
    if (path === '/caes' || path === '/caes/') {
      return 'caes';
    }
    // IMPL-031 · /calculadora → Calculadora de Ahorro (público)
    if (path === '/calculadora' || path === '/calculadora/') {
      return 'calculadora';
    }
    // Mockup temporal para visualizar
    if (path === '/mockup' || path === '/mockup/') {
      return 'mockup';
    }
    // Mockup fondo blanco
    if (path === '/mockup-blanco' || path === '/mockup-blanco/') {
      return 'mockup-blanco';
    }
    // Mockup fondo blanco V2 (estilo mobilsafe.es)
    if (path === '/mockup-blanco-v2' || path === '/mockup-blanco-v2/') {
      return 'mockup-blanco-v2';
    }
    // Mockup V2 Real (pantallas reales con estilo mobilsafe.es)
    if (path === '/mockup-v2' || path === '/mockup-v2/') {
      return 'mockup-v2-real';
    }
    // Mockup V2 con Fotos (Opción D)
    if (path === '/mockup-v2-fotos' || path === '/mockup-v2-fotos/') {
      return 'mockup-v2-fotos';
    }
    // Mockup V3 (fotos seleccionadas: autobuses, camión, conductor)
    if (path === '/mockup-v3' || path === '/mockup-v3/') {
      return 'mockup-v3';
    }
    // Mockup comparativo de 3 variantes para destacar el TOTAL en P5 calculadora
    if (path === '/mockup-calc-total' || path === '/mockup-calc-total/') {
      return 'mockup-calc-total';
    }
    // Mockup tablas acordeón ConstructorOferta (B1/B2/etc) — IMPL-039 propuesta
    if (path === '/mockup-tablas' || path === '/mockup-tablas/') {
      return 'mockup-tablas';
    }
    // Mockup layout narrativo ConstructorOferta Paso 2 (3 bloques + panel interno)
    if (path === '/mockup-layout' || path === '/mockup-layout/') {
      return 'mockup-layout';
    }
    // Informe CAES personalizado
    if (path.startsWith('/informe/')) {
      return 'informe';
    }
    // Verificar rutas de área: /area/seguridad, /area/combustible, etc.
    if (path.startsWith('/area/')) {
      return 'area';
    }
    // Configuración del Constructor de Ofertas (separada del legacy /admin)
    if (path === '/admin/constructor/configuracion' || path === '/admin/constructor/configuracion/') {
      return 'configuracion-constructor';
    }
    // Administración global (config, usuarios, analytics, etc.)
    if (path === '/admin/administracion' || path === '/admin/administracion/') {
      return 'admin-panel';
    }
    // Prospección pública (oportunidades visibles para todos los usuarios autenticados)
    if (path === '/admin/prospeccion' || path === '/admin/prospeccion/') {
      return 'prospeccion';
    }
    // Clientes — Zona 2 Desarrollo Comercial
    if (path === '/admin/clientes' || path === '/admin/clientes/') {
      return 'clientes';
    }
    // Reuniones — Zona 2 listado global (Paso 3 spec v3.2.A)
    if (path === '/admin/reuniones' || path === '/admin/reuniones/') {
      return 'reuniones';
    }
    // Ofertas — Zona 2 índice global (Paso 6 spec v3.2.A)
    if (path === '/admin/ofertas' || path === '/admin/ofertas/') {
      return 'ofertas';
    }
    // Zona 1 · Captación → AdminPanel legacy
    if (path === '/admin/zona1' || path === '/admin/zona1/') {
      return 'zona1';
    }
    // Operaciones SaaS · quinta zona
    if (path === '/admin/saas/administracion' || path === '/admin/saas/administracion/') {
      return 'saas-admin';
    }
    if (path === '/admin/saas' || path === '/admin/saas/' || path.startsWith('/admin/saas/')) {
      return 'saas';
    }
    // /admin raíz → MenuZonas (o login si no autenticado)
    if (path === '/admin' || path === '/admin/') {
      return 'admin';
    }
    return 'landing';
  };

  const getInitialArea = () => {
    const path = window.location.pathname;
    if (path.startsWith('/area/')) {
      return path.replace('/area/', '').replace('/', '');
    }
    return null;
  };

  const [page, setPage] = useState(getInitialPage);
  const [config, setConfig] = useState(null);
  const [comerciales, setComerciales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [diagnostico, setDiagnostico] = useState(null);
  const [diagnosticoRespuestas, setDiagnosticoRespuestas] = useState({});
  const [resetToken, setResetToken] = useState(null);
  const [selectedArea, setSelectedArea] = useState(getInitialArea);

  // Función para cambiar de página y actualizar la URL
  const navigateTo = (newPage, updateUrl = true) => {
    setPage(newPage);
    if (updateUrl) {
      const hostname = window.location.hostname.toLowerCase();
      let newPath = '/';
      
      // Determinar la ruta según la página
      if (hostname.includes('tuingresocaes') || hostname.includes('tuingreso')) {
        // En el subdominio tuingresocaes
        if (newPage === 'landing') newPath = '/analisis';
        else if (newPage === 'caes') newPath = '/';
        else if (newPage === 'login' || newPage === 'admin') newPath = '/admin';
      } else {
        // En otros dominios
        if (newPage === 'caes') newPath = '/caes';
        else if (newPage === 'landing') newPath = '/';
        else if (newPage === 'login') newPath = '/admin';
        else if (newPage === 'admin' || newPage === 'menu-zonas') newPath = '/admin';
        else if (newPage === 'zona1') newPath = '/admin/zona1';
        else if (newPage === 'admin-panel') newPath = '/admin/administracion';
        else if (newPage === 'prospeccion') newPath = '/admin/prospeccion';
        else if (newPage === 'configuracion-constructor') newPath = '/admin/constructor/configuracion';
        else if (newPage === 'clientes') newPath = '/admin/clientes';
        else if (newPage === 'reuniones') newPath = '/admin/reuniones';
        else if (newPage === 'ofertas') newPath = '/admin/ofertas';
        else if (newPage === 'saas') newPath = '/admin/saas/inicio';
        else if (newPage === 'saas-admin') newPath = '/admin/saas/administracion';
      }
      
      window.history.pushState({page: newPage}, '', newPath);
    }
  };

  // Escuchar navegación del navegador (botón atrás/adelante)
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state?.page) {
        setPage(event.state.page);
      } else {
        setPage(getInitialPage());
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Scroll arriba cuando cambia la página
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  useEffect(() => { 
    // Verificar si hay un token de reset en la URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('reset_token');
    if (token) {
      setResetToken(token);
      setPage('reset-password');
      // Limpiar el token de la URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    loadData(); 
  }, []);

  const loadData = async () => {
    try {
      const configRes = await axios.get(`${API}/config`);
      setConfig(configRes.data);
      // No cargar comerciales aquí, requiere auth
    } catch (error) {
      console.error('Error loading config:', error);
    }
    setLoading(false);
  };

  const handleConfigUpdate = (newConfig) => {
    setConfig(newConfig);
  };

  const handleClientContinue = (data) => {
    setClientData(data);
    if (data.tipoAnalisis === 'consultivo') {
      window.history.pushState({page: 'diagnostico'}, '', window.location.pathname);
      setPage('diagnostico');
    } else if (data.tipoAnalisis === 'quick') {
      window.history.pushState({page: 'quick'}, '', window.location.pathname);
      setPage('quick');
    } else {
      window.history.pushState({page: 'simulator'}, '', window.location.pathname);
      setPage('simulator');
    }
  };

  const handleDiagnosticoContinue = (diag) => {
    setDiagnostico(diag);
    window.history.pushState({page: 'simulator'}, '', window.location.pathname);
    setPage('simulator');
  };

  const handleQuickFinish = (quickFormData, quickResultado) => {
    setClientData(prev => ({
      ...prev,
      quickData: quickFormData,
      quickResultado: quickResultado
    }));
    window.history.pushState({page: 'simulator'}, '', window.location.pathname);
    setPage('simulator');
  };

  // Auth como estado React: un tick que se incrementa en login/logout
  // para forzar rerender cuando navigateTo apunta a la misma page que ya estaba.
  const readAuth = () => {
    const token = localStorage.getItem('mobilsafe_token');
    const auth = localStorage.getItem('mobilsafe_auth');
    return !!token && auth === 'true';
  };
  const [authed, setAuthed] = useState(readAuth);
  const isAuthenticated = () => authed;

  // Iter52.58b · 10 · Cambio de contraseña.
  //  - `debeCambiar`: primer acceso, pantalla obligatoria (10b).
  //  - `cambiarPass`: panel abierto desde el perfil (10a); cualquier
  //    cabecera lo dispara con el evento global `abrir-cambiar-password`.
  const leerDebeCambiar = () => {
    try {
      return !!JSON.parse(localStorage.getItem('mobilsafe_user') || '{}').debe_cambiar_password;
    } catch { return false; }
  };
  const [debeCambiar, setDebeCambiar] = useState(leerDebeCambiar);
  const [cambiarPass, setCambiarPass] = useState(false);
  useEffect(() => {
    const abrir = () => setCambiarPass(true);
    window.addEventListener('abrir-cambiar-password', abrir);
    return () => window.removeEventListener('abrir-cambiar-password', abrir);
  }, []);

  // Llamado por LoginPanel tras guardar token+user en localStorage.
  const handleLoginSuccess = (target = 'admin') => {
    setAuthed(true);
    setDebeCambiar(leerDebeCambiar());
    navigateTo(target);
  };

  const handleLogout = () => {
    localStorage.removeItem('mobilsafe_token');
    localStorage.removeItem('mobilsafe_user');
    localStorage.removeItem('mobilsafe_auth');
    setAuthed(false);
    setPage('landing');
  };

  // Tracking de página actual (debe estar antes de cualquier return condicional)
  usePageTracking(page);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center">
        <RefreshCw className="w-12 h-12 text-emerald-400 animate-spin mx-auto" />
      </div>
    );
  }

  // Iter52.58b · 10b · Mientras deba estrenar contraseña, la app solo
  // muestra esta pantalla (el backend bloquea el resto con 403).
  if (authed && debeCambiar) {
    return (
      <CambiarPasswordPanel forzado onDone={() => {
        setDebeCambiar(false);
        navigateTo('menu-zonas');
      }} />
    );
  }

  return (
    <AppContext.Provider value={{ config, setConfig, comerciales }}>
      <div className="App">
        <Toaster position="top-right" richColors />
        {cambiarPass && <CambiarPasswordPanel onClose={() => setCambiarPass(false)}
                                              onDone={() => setCambiarPass(false)} />}
        
        {page === 'landing' && (
          <Landing 
            onStart={() => navigateTo('entry', false)} 
            onAdmin={() => isAuthenticated() ? navigateTo('admin') : navigateTo('login')} 
            onCAES={() => navigateTo('caes')}
            onAreaClick={(areaId) => {
              setSelectedArea(areaId);
              navigateTo('area', false);
            }}
          />
        )}

        {page === 'area' && selectedArea && (
          <AreaLanding 
            areaId={selectedArea}
            onBack={() => navigateTo('landing')}
            onSimular={() => navigateTo('entry', false)}
          />
        )}
        
        {page === 'login' && (
          <LoginPanel 
            onLogin={() => handleLoginSuccess('admin')} 
            onBack={() => navigateTo('landing')}
            onForgotPassword={() => setPage('forgot-password')}
          />
        )}
        
        {page === 'forgot-password' && (
          <PasswordReset 
            onBack={() => setPage('login')} 
          />
        )}
        
        {page === 'reset-password' && resetToken && (
          <PasswordReset 
            onBack={() => setPage('login')} 
            resetToken={resetToken}
          />
        )}
        
        {page === 'caes' && config && (
          <CAESLanding 
            config={config} 
            onBack={() => navigateTo('landing')} 
          />
        )}

        {/* IMPL-031 · Calculadora de Ahorro pública */}
        {page === 'calculadora' && (
          <CalculadoraAhorro />
        )}

        {page === 'mockup' && (
          <MockupResultadoParcial />
        )}

        {page === 'mockup-blanco' && (
          <MockupFondoBlanco />
        )}

        {page === 'mockup-blanco-v2' && (
          <MockupFondoBlancoV2 />
        )}

        {page === 'mockup-v2-real' && (
          <MockupV2Real />
        )}

        {page === 'mockup-v2-fotos' && (
          <MockupV2ConFotos />
        )}

        {page === 'mockup-v3' && (
          <MockupV3 />
        )}

        {page === 'mockup-calc-total' && (
          <MockupCalcTotal />
        )}

        {page === 'mockup-tablas' && (
          <MockupTablasAcordeon />
        )}

        {page === 'mockup-layout' && (
          <MockupConstructorLayout />
        )}

        {page === 'informe' && (
          <InformeCAES />
        )}
        
        {page === 'admin' && isAuthenticated() && (
          <MenuZonas
            user={(() => { try { return JSON.parse(localStorage.getItem('mobilsafe_user') || 'null'); } catch { return null; } })()}
            onOpenZona1={() => navigateTo('zona1')}
            onOpenZona2={() => navigateTo('clientes')}
            onOpenConfig={() => navigateTo('configuracion-constructor')}
            onOpenAdmin={() => navigateTo('admin-panel')}
            onOpenProspeccion={() => navigateTo('prospeccion')}
            onOpenSaaS={() => navigateTo('saas')}
            onLogout={handleLogout}
          />
        )}

        {page === 'zona1' && isAuthenticated() && (
          <AdminPanel
            onClose={() => navigateTo('admin')}
            config={config}
            onConfigUpdate={handleConfigUpdate}
            comerciales={comerciales}
            onLogout={handleLogout}
            onOpenConfigConstructor={() => navigateTo('configuracion-constructor')}
            onOpenClientes={() => navigateTo('clientes')}
            modo="zona1"
          />
        )}

        {page === 'zona1' && !isAuthenticated() && (
          <LoginPanel onLogin={() => handleLoginSuccess('zona1')} onBack={() => navigateTo('landing')} onForgotPassword={() => setPage('forgot-password')} />
        )}

        {page === 'admin-panel' && isAuthenticated() && (
          <AdminPanel
            onClose={() => navigateTo('admin')}
            config={config}
            onConfigUpdate={handleConfigUpdate}
            comerciales={comerciales}
            onLogout={handleLogout}
            onOpenConfigConstructor={() => navigateTo('configuracion-constructor')}
            onOpenClientes={() => navigateTo('clientes')}
            modo="admin"
          />
        )}

        {page === 'admin-panel' && !isAuthenticated() && (
          <LoginPanel onLogin={() => handleLoginSuccess('admin-panel')} onBack={() => navigateTo('landing')} onForgotPassword={() => setPage('forgot-password')} />
        )}

        {page === 'prospeccion' && isAuthenticated() && (
          <ProspeccionPublica onBack={() => navigateTo('admin')} onLogout={handleLogout} />
        )}
        {page === 'prospeccion' && !isAuthenticated() && (
          <LoginPanel onLogin={() => handleLoginSuccess('prospeccion')} onBack={() => navigateTo('landing')} onForgotPassword={() => setPage('forgot-password')} />
        )}

        {page === 'configuracion-constructor' && isAuthenticated() && (
          <ConfiguracionConstructor onBack={() => navigateTo('admin')} />
        )}

        {page === 'configuracion-constructor' && !isAuthenticated() && (
          <LoginPanel onLogin={() => handleLoginSuccess('configuracion-constructor')} onBack={() => navigateTo('landing')} onForgotPassword={() => setPage('forgot-password')} />
        )}

        {page === 'clientes' && isAuthenticated() && (
          <Clientes onBack={() => navigateTo('admin')} />
        )}

        {page === 'clientes' && !isAuthenticated() && (
          <LoginPanel onLogin={() => handleLoginSuccess('clientes')} onBack={() => navigateTo('landing')} onForgotPassword={() => setPage('forgot-password')} />
        )}

        {page === 'reuniones' && isAuthenticated() && (
          <Reuniones onBack={() => navigateTo('admin')} />
        )}
        {page === 'reuniones' && !isAuthenticated() && (
          <LoginPanel onLogin={() => handleLoginSuccess('reuniones')} onBack={() => navigateTo('landing')} onForgotPassword={() => setPage('forgot-password')} />
        )}

        {page === 'saas' && isAuthenticated() && (
          <SaaSModule onLogout={handleLogout} />
        )}
        {page === 'saas' && !isAuthenticated() && (
          <LoginPanel onLogin={() => handleLoginSuccess('saas')} onBack={() => navigateTo('landing')} onForgotPassword={() => setPage('forgot-password')} />
        )}
        {page === 'saas-admin' && isAuthenticated() && (
          <SaaSAdmin onLogout={handleLogout} />
        )}
        {page === 'saas-admin' && !isAuthenticated() && (
          <LoginPanel onLogin={() => handleLoginSuccess('saas-admin')} onBack={() => navigateTo('landing')} onForgotPassword={() => setPage('forgot-password')} />
        )}

        {page === 'admin' && !isAuthenticated() && (
          <LoginPanel 
            onLogin={() => handleLoginSuccess('admin')} 
            onBack={() => navigateTo('landing')} 
            onForgotPassword={() => setPage('forgot-password')}
          />
        )}
        
        {page === 'entry' && config && (
          <ClientEntry 
            config={config} 
            onContinue={handleClientContinue} 
            onBack={() => navigateTo('landing')} 
          />
        )}
        
        {page === 'diagnostico' && clientData && (
          <CuestionarioDiagnostico 
            clientData={clientData} 
            respuestas={diagnosticoRespuestas} 
            setRespuestas={setDiagnosticoRespuestas} 
            onContinue={handleDiagnosticoContinue} 
            onBack={() => {
              window.history.pushState({page: 'entry'}, '', window.location.pathname);
              setPage('entry');
            }} 
          />
        )}
        
        {page === 'quick' && config && clientData && (
          <QuickAnalysis 
            config={config} 
            clientData={clientData} 
            comerciales={comerciales} 
            onBack={() => {
              window.history.pushState({page: 'entry'}, '', window.location.pathname);
              setPage('entry');
            }} 
            onFinish={handleQuickFinish} 
          />
        )}
        
        {page === 'simulator' && config && clientData && (
          <Simulator 
            config={config} 
            clientData={clientData} 
            diagnostico={diagnostico} 
            onBack={() => { 
              if (clientData.tipoAnalisis === 'consultivo') {
                window.history.pushState({page: 'diagnostico'}, '', window.location.pathname);
                setPage('diagnostico');
              } else if (clientData.tipoAnalisis === 'quick') {
                window.history.pushState({page: 'quick'}, '', window.location.pathname);
                setPage('quick');
              } else {
                window.history.pushState({page: 'entry'}, '', window.location.pathname);
                setPage('entry');
              }
            }} 
          />
        )}

        {/* Cookie Banner - RGPD */}
        <CookieBanner />

        {/* IMPL-040 (2026-05-13) · Botón flotante WhatsApp · solo en páginas públicas (no admin/login) */}
        {['caes', 'landing', 'area', 'simulator', 'entry', 'diagnostico', 'quick'].includes(page) && <WhatsAppButton />}
      </div>
    </AppContext.Provider>
  );
}

// IMPL-040 (2026-05-13) · Botón flotante WhatsApp para captación desde landings públicas.
function WhatsAppButton() {
  return (
    <a
      href="https://wa.me/34648671833?text=Hola%20%F0%9F%91%8B%20Gestiono%20una%20flota%20y%20quiero%20ver%20cu%C3%A1nto%20puedo%20ahorrar%20con%20MOBILSAFE.%20%C2%BFMe%20pod%C3%A9is%20ayudar%3F"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Hablar con MOBILSAFE por WhatsApp"
      data-testid="whatsapp-float-btn"
      style={{
        position: 'fixed', bottom: '16px', right: '16px', zIndex: 2147483647,
        background: '#25D366', color: 'white', borderRadius: '999px',
        padding: '12px 22px', minWidth: '210px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        fontWeight: 600, fontSize: '14px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)', textDecoration: 'none',
        transition: 'transform 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/>
      </svg>
      ¿Hablamos?
    </a>
  );
}

// Wrapper de página: oportunidades de Prospección accesibles a todos los autenticados
function ProspeccionPublica({ onBack, onLogout }) {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-sm font-bold truncate">Prospección</span>
            <span className="text-xs text-indigo-100 hidden md:inline">Leads outbound · IA</span>
          </div>
          <ZonaNav activa="prospeccion" onLogout={onLogout} esAdmin={true} />
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-6">
        <ProspeccionPanel mode="oportunidades" />
      </div>
    </div>
  );
}

export default App;
